/**
 * Card gallery hover/tap zoom preview.
 *
 * Desktop: hovering a product image for HOVER_DELAY ms grows it into a large
 * preview overlay. The preview closes again as soon as the user scrolls, or
 * moves the mouse more than MOVE_THRESHOLD px from where it was when the
 * preview opened (so the user doesn't have to chase the enlarged image to
 * leave it).
 *
 * Touch: the first tap opens the preview instead of navigating. Tapping the
 * enlarged preview navigates to the product, tapping outside it closes it.
 */
(() => {
  if (window.__cardGalleryZoomInit) return;
  window.__cardGalleryZoomInit = true;

  const HOVER_DELAY = 1500;
  const CLOSE_DELAY = 100;
  const MOVE_THRESHOLD = 70;

  let backdrop = null;
  let activeClone = null;
  let activeGallery = null;
  let hoverTimer = null;
  let closeTimer = null;
  let lastPointer = { x: 0, y: 0 };
  let originPointer = null;

  const isTouch = () => window.matchMedia('(hover: none)').matches;

  const getTargetRect = () => {
    const headerHeight =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 60;
    const sideMargin = Math.max(24, window.innerWidth * 0.05);
    const top = headerHeight + Math.max(16, window.innerHeight * 0.03);
    const bottom = Math.max(64, window.innerHeight * 0.08);

    return {
      top,
      left: sideMargin,
      width: window.innerWidth - sideMargin * 2,
      height: window.innerHeight - top - bottom,
    };
  };

  // Created up front (and kept as the first element in <body>) so any later
  // preview clone is always appended after it and therefore renders on top.
  const ensureBackdrop = () => {
    if (backdrop) return backdrop;
    backdrop = document.createElement('div');
    backdrop.className = 'card-gallery-zoom-backdrop';
    backdrop.addEventListener('click', () => closeZoom());
    document.body.prepend(backdrop);
    return backdrop;
  };

  const cancelClose = () => {
    clearTimeout(closeTimer);
    closeTimer = null;
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer = setTimeout(() => closeZoom(), CLOSE_DELAY);
  };

  function openZoom(gallery) {
    if (activeGallery === gallery) return;

    const image = gallery.querySelector('slideshow-slide[aria-hidden="false"] img, img');
    const link = gallery.querySelector('a[href]');
    if (!image || !link) return;

    closeZoom(true);
    ensureBackdrop();

    const startRect = image.getBoundingClientRect();

    const clone = document.createElement('a');
    clone.href = link.href;
    clone.className = 'card-gallery-zoom-clone';
    const ariaLabel = link.getAttribute('aria-label');
    if (ariaLabel) clone.setAttribute('aria-label', ariaLabel);

    const cloneImage = document.createElement('img');
    cloneImage.alt = image.alt || '';
    cloneImage.src = image.currentSrc || image.src;
    if (image.srcset) cloneImage.srcset = image.srcset;
    clone.appendChild(cloneImage);

    clone.style.top = `${startRect.top}px`;
    clone.style.left = `${startRect.left}px`;
    clone.style.width = `${startRect.width}px`;
    clone.style.height = `${startRect.height}px`;

    document.body.appendChild(clone);
    const bd = ensureBackdrop();

    clone.addEventListener('mouseenter', cancelClose);
    clone.addEventListener('mouseleave', () => {
      if (!isTouch()) scheduleClose();
    });

    requestAnimationFrame(() => {
      bd.classList.add('is-active');
      const target = getTargetRect();
      clone.style.top = `${target.top}px`;
      clone.style.left = `${target.left}px`;
      clone.style.width = `${target.width}px`;
      clone.style.height = `${target.height}px`;
      clone.classList.add('is-zoomed');
    });

    activeClone = clone;
    activeGallery = gallery;
    originPointer = { ...lastPointer };
  }

  function closeZoom(immediate) {
    cancelClose();
    clearTimeout(hoverTimer);
    hoverTimer = null;
    originPointer = null;

    if (!activeClone) return;

    const clone = activeClone;
    activeClone = null;
    activeGallery = null;

    if (backdrop) {
      if (immediate) {
        // Drop the dimmed overlay instantly, without waiting for its
        // opacity transition to finish (e.g. when closing due to scroll).
        backdrop.style.transition = 'none';
        backdrop.classList.remove('is-active');
        requestAnimationFrame(() => {
          backdrop.style.transition = '';
        });
      } else {
        backdrop.classList.remove('is-active');
      }
    }

    if (immediate) {
      clone.remove();
      return;
    }

    clone.classList.remove('is-zoomed');
    clone.classList.add('is-closing');
    clone.addEventListener('transitionend', () => clone.remove(), { once: true });
    setTimeout(() => clone.remove(), 500);
  }

  document.addEventListener(
    'mouseover',
    (event) => {
      if (isTouch()) return;

      const gallery = event.target.closest('.card-gallery');
      if (!gallery || gallery === activeGallery) return;

      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(() => openZoom(gallery), HOVER_DELAY);
    },
    true
  );

  document.addEventListener(
    'mouseout',
    (event) => {
      if (isTouch()) return;

      const gallery = event.target.closest('.card-gallery');
      if (!gallery) return;

      const related = event.relatedTarget;
      if (related && gallery.contains(related)) return;

      clearTimeout(hoverTimer);
      hoverTimer = null;

      if (activeGallery === gallery) scheduleClose();
    },
    true
  );

  // Track pointer position and close the preview as soon as the mouse
  // moves more than a small distance away from where it opened.
  document.addEventListener(
    'mousemove',
    (event) => {
      lastPointer = { x: event.clientX, y: event.clientY };

      if (!activeClone || isTouch() || !originPointer) return;

      const dx = event.clientX - originPointer.x;
      const dy = event.clientY - originPointer.y;
      if (Math.sqrt(dx * dx + dy * dy) > MOVE_THRESHOLD) {
        closeZoom();
      }
    },
    true
  );

  // Close immediately on scroll (any direction) so the preview never floats
  // over content that has moved underneath it.
  window.addEventListener(
    'scroll',
    () => {
      if (activeClone) closeZoom(true);
    },
    { passive: true, capture: true }
  );

  document.addEventListener(
    'click',
    (event) => {
      if (!isTouch()) return;

      if (activeClone) {
        if (!event.target.closest('.card-gallery-zoom-clone')) {
          event.preventDefault();
          closeZoom();
        }
        return;
      }

      const gallery = event.target.closest('.card-gallery');
      if (gallery) {
        event.preventDefault();
        openZoom(gallery);
      }
    },
    true
  );
})();
